// Response side of `POST /search/` with `with_sources=true`. Request building
// stays with each caller, since their bodies and params differ.

import { recordHome, recordHomeUrl, recordTitle } from "src/api/records";
import type { RecordInfo } from "src/api/records";

/** A matched chunk, with every field type-checked. */
export interface SearchChunk {
  id?: number;
  chunk_data?: string;
}

/** A matched chunk's source data record, with every field type-checked. */
export interface SearchSourceRecord {
  id?: number;
  data: Record<string, unknown>;
  /** Negative for a replicated dataset. */
  dataset_fk?: number;
  project_folder_fk?: number;
  /** Absent on the chain's current revision. */
  superseded_date?: string;
  revision_label?: string;
  version_num?: number;
  plant?: string;
  create_date?: string;
  /** `dataset_name`, else `dataset.name`. */
  dataset_name?: string;
  /** `project_folder_name`, else `project_folder.name`. */
  project_folder_name?: string;
}

export interface SearchHit {
  chunk: SearchChunk;
  record: SearchSourceRecord;
  /** Index in the response's result list, counting malformed entries. */
  position: number;
}

export interface SearchResponse {
  hits: SearchHit[];
  /** Length of the response's result list, malformed entries included. */
  rawCount: number;
  expandedTerms: string[];
  matchedCount?: number;
  /** The envelope object; empty for a bare result list. */
  envelope: Record<string, unknown>;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const num = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

/** The wire sends `chunk_data` as `{data: "<text>"}`; a bare string is
 *  accepted too, in case a caller ever sends the text directly. */
const chunkText = (value: unknown): string | undefined =>
  str(value) ?? str(asRecord(value).data);

const parseChunk = (value: unknown): SearchChunk => {
  const chunk = asRecord(value);
  return { id: num(chunk.id), chunk_data: chunkText(chunk.chunk_data) };
};

const parseSourceRecord = (value: unknown): SearchSourceRecord => {
  const sdr = asRecord(value);
  return {
    id: num(sdr.id),
    data: asRecord(sdr.data),
    dataset_fk: num(sdr.dataset_fk),
    project_folder_fk: num(sdr.project_folder_fk),
    superseded_date:
      sdr.superseded_date == null ? undefined : String(sdr.superseded_date),
    revision_label: str(sdr.revision_label),
    version_num: num(sdr.version_num),
    plant: str(sdr.plant),
    create_date: str(sdr.create_date),
    dataset_name: str(sdr.dataset_name) ?? str(asRecord(sdr.dataset).name),
    project_folder_name:
      str(sdr.project_folder_name) ?? str(asRecord(sdr.project_folder).name),
  };
};

/**
 * Parse the bare `[chunk, record]` list or the `include_expanded_terms`
 * envelope, skipping entries that are not a two-element tuple.
 */
export const parseSearchResults = (data: unknown): SearchResponse => {
  const envelope = asRecord(data);
  const rawResults: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(envelope.results)
      ? envelope.results
      : [];
  const hits: SearchHit[] = [];
  rawResults.forEach((entry, position) => {
    if (!Array.isArray(entry) || entry.length < 2) return;
    hits.push({
      chunk: parseChunk(entry[0]),
      record: parseSourceRecord(entry[1]),
      position,
    });
  });
  const expandedTerms = (
    Array.isArray(envelope.expanded_terms) ? envelope.expanded_terms : []
  ).filter((term): term is string => typeof term === "string");
  return {
    hits,
    rawCount: rawResults.length,
    expandedTerms,
    matchedCount: num(envelope.matched_count),
    envelope,
  };
};

/**
 * `recordTitle` first, so a hit reads the same as the record does everywhere
 * else. `data.title` sits below it because it mostly duplicates
 * `extracted_metadata.title`, and on a tabular record is just a column.
 */
export const searchResultTitle = (record: SearchSourceRecord): string =>
  str(recordTitle(record as unknown as RecordInfo)) ??
  str(record.data.title) ??
  str(record.data.name) ??
  (record.id !== undefined ? `Document ${record.id}` : "Untitled document");

/** The record's document type, if it declares one. */
export const searchResultKind = (
  record: SearchSourceRecord,
): string | undefined =>
  str(asRecord(record.data.extracted_metadata).document_type) ??
  str(record.data.document_type) ??
  str(record.data.file_type);

/** Name of the dataset or folder the record lives in, when the wire carries it. */
export const searchResultLocation = (
  record: SearchSourceRecord,
): string | undefined => record.dataset_name ?? record.project_folder_name;

/**
 * The record route for a hit, landing on the matched chunk; undefined when
 * the record has no id or no resolvable home.
 */
export const searchResultHref = (
  chunk: SearchChunk,
  record: SearchSourceRecord,
  query: string,
): string | undefined => {
  const home = recordHome(record);
  return record.id !== undefined && home
    ? recordHomeUrl(home, record.id, { chunkId: chunk.id, search: query })
    : undefined;
};
